import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../api/Axios';

const UserPermissionsDebug = () => {
    const { user, userId } = useAuth();
    const [permissions, setPermissions] = useState({
        directPermissions: [],
        templatePermissions: [],
        effectivePermissions: [],
        templates: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        fetchUserPermissions();
    }, [userId]);

    const fetchUserPermissions = async () => {
        try {
            setLoading(true);
            
            // Use the original working endpoint
            if (userId) {
                const response = await axiosInstance.get(`/api/accounts/user-permissions/${userId}/`);
                const data = response.data;
                
                console.log('Debug: Raw response data:', data);
                
                setPermissions({
                    directPermissions: data.individual_permissions || [],
                    templatePermissions: data.effective_permissions || [],
                    effectivePermissions: data.effective_permissions || [],
                    templates: data.permission_templates || []
                });
            } else {
                console.warn('No userId available for permissions fetch');
                setError('No user ID available');
            }
        } catch (error) {
            console.error('Error fetching user permissions:', error);
            setError(`Failed to load permissions: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const getAllUniquePermissions = () => {
        const directPerms = permissions.directPermissions.map(p => ({ ...p, source: 'Direct' }));
        const templatePerms = permissions.templatePermissions.map(p => ({ ...p, source: 'Template' }));
        
        // Combine and deduplicate
        const allPerms = [...directPerms, ...templatePerms];
        const uniquePerms = allPerms.reduce((acc, perm) => {
            const existing = acc.find(p => p.id === perm.id);
            if (existing) {
                // If permission exists, combine sources
                if (!existing.source.includes(perm.source)) {
                    existing.source += `, ${perm.source}`;
                }
            } else {
                acc.push(perm);
            }
            return acc;
        }, []);
        
        return uniquePerms.sort((a, b) => a.name.localeCompare(b.name));
    };

    if (loading) {
        return (
            <div className="permissions-debug loading">
                <div className="debug-header">
                    <h5>🔧 Debug: Loading user permissions...</h5>
                </div>
            </div>
        );
    }

    const uniquePermissions = getAllUniquePermissions();

    return (
        <div className="permissions-debug">
            <div className="debug-header" onClick={() => setIsExpanded(!isExpanded)}>
                <h5 style={{ margin: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    🔧 Debug: User Permissions ({uniquePermissions.length} total)
                    <span style={{ fontSize: '12px' }}>
                        {isExpanded ? '▼ Click to collapse' : '▶ Click to expand'}
                    </span>
                </h5>
            </div>
            
            {isExpanded && (
                <div className="debug-content">
                    <div className="debug-section">
                        <h6>📋 Templates Applied ({permissions.templates.length})</h6>
                        {permissions.templates.length > 0 ? (
                            <ul className="template-list">
                                {permissions.templates.map(template => (
                                    <li key={template.id} className="template-item">
                                        <strong>{template.name}</strong> - {template.permissions.length} permissions
                                        <small style={{ display: 'block', color: '#666' }}>
                                            {template.description}
                                        </small>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <p style={{ color: '#666', fontSize: '14px' }}>No templates applied</p>
                        )}
                    </div>

                    <div className="debug-section">
                        <h6>🔑 All Effective Permissions</h6>
                        {uniquePermissions.length > 0 ? (
                            <div className="permissions-grid">
                                {uniquePermissions.map(permission => (
                                    <div key={permission.id} className="permission-item">
                                        <div className="permission-name">{permission.name}</div>
                                        <div className="permission-details">
                                            <span className="permission-model">{permission.content_type__model}</span>
                                            <span className={`permission-source source-${permission.source.toLowerCase()}`}>
                                                {permission.source}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p style={{ color: '#666', fontSize: '14px' }}>No permissions assigned</p>
                        )}
                    </div>

                    <div className="debug-section">
                        <h6>📊 Summary</h6>
                        <div className="summary-stats">
                            <div className="stat-item">
                                <span className="stat-label">Direct Permissions:</span>
                                <span className="stat-value">{permissions.directPermissions.length}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Template Permissions:</span>
                                <span className="stat-value">{permissions.templatePermissions.length}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">Total Unique:</span>
                                <span className="stat-value">{uniquePermissions.length}</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">User Type:</span>
                                <span className="stat-value">{user?.user_type || 'Unknown'}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .permissions-debug {
                    background: #f8f9fa;
                    border: 2px solid #007bff;
                    border-radius: 8px;
                    margin-bottom: 20px;
                    font-family: 'Courier New', monospace;
                }

                .permissions-debug.loading {
                    padding: 15px;
                    text-align: center;
                }

                .debug-header {
                    background: #007bff;
                    color: white;
                    padding: 12px 15px;
                    border-radius: 6px 6px 0 0;
                    user-select: none;
                }

                .debug-header:hover {
                    background: #0056b3;
                }

                .debug-content {
                    padding: 15px;
                    max-height: 400px;
                    overflow-y: auto;
                }

                .debug-section {
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 1px solid #dee2e6;
                }

                .debug-section:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                }

                .debug-section h6 {
                    margin: 0 0 10px 0;
                    color: #495057;
                    font-size: 14px;
                    font-weight: bold;
                }

                .template-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .template-item {
                    background: #e9ecef;
                    padding: 8px 12px;
                    margin-bottom: 5px;
                    border-radius: 4px;
                    font-size: 13px;
                }

                .permissions-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
                    gap: 8px;
                }

                .permission-item {
                    background: white;
                    padding: 8px 10px;
                    border-radius: 4px;
                    border: 1px solid #dee2e6;
                    font-size: 12px;
                }

                .permission-name {
                    font-weight: bold;
                    color: #212529;
                    margin-bottom: 4px;
                }

                .permission-details {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .permission-model {
                    color: #6c757d;
                    font-style: italic;
                }

                .permission-source {
                    padding: 2px 6px;
                    border-radius: 3px;
                    font-size: 10px;
                    font-weight: bold;
                    text-transform: uppercase;
                }

                .source-direct {
                    background: #d4edda;
                    color: #155724;
                }

                .source-template {
                    background: #d1ecf1;
                    color: #0c5460;
                }

                .source-direct,template {
                    background: #fff3cd;
                    color: #856404;
                }

                .summary-stats {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
                    gap: 10px;
                }

                .stat-item {
                    display: flex;
                    justify-content: space-between;
                    padding: 5px 8px;
                    background: white;
                    border-radius: 4px;
                    border: 1px solid #dee2e6;
                }

                .stat-label {
                    color: #6c757d;
                    font-size: 12px;
                }

                .stat-value {
                    font-weight: bold;
                    color: #212529;
                    font-size: 12px;
                }

                @media (max-width: 768px) {
                    .permissions-grid {
                        grid-template-columns: 1fr;
                    }
                    
                    .summary-stats {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default UserPermissionsDebug;
